import { capitalize } from 'utils/helpers';
import { getEarnestComissionsData } from 'graphql/earnest-and-comissions/queries';
import { useSocketAccountingEventInvalidator } from 'hooks/useSocketAccountingEventInvalidator';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { OrderProperty } from 'types/order';
import useAlert from 'utils/alert';
import { LEDGER_CODE_PURCHASE_PRICE } from 'utils/constants';
import useDeleteLedgerAssociation from 'utils/ledger/use-delete-ledger-association';
import { AccountingInvolvedParty, AccountingType } from 'v2-types/order';

import { initialValues } from '../properties/utils/initialValues';

const useEarnestAndCommissions = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [selectedProperty, setSelectedProperty] = useState<OrderProperty>(initialValues);
  const [openPartyLedgerModal, setOpenPartyLedgerModal] = useState<boolean>(false);
  const showAlert = useAlert();

  const [properties, setProperties] = useState<OrderProperty[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);

  const [partyLedgerEntry, setPartyLedgerEntry] = useState<AccountingInvolvedParty>();
  const [pendingDeleteEntry, setPendingDeleteEntry] = useState<AccountingInvolvedParty>();

  useSocketAccountingEventInvalidator({ queries: ['earnest-commissions-data', orderId] });

  const { data } = useQuery(['earnest-commissions-data', orderId], () => getEarnestComissionsData({ id: orderId, filters: { accounting: "property" } }), {
    onSuccess: (response) => {
      setProperties(response.getOrder.estates ?? []);

      if (response.getOrder && response.getOrder.estates?.length) {
        setSelectedProperty(response.getOrder.estates[0]);
      }
    },
    refetchOnWindowFocus: false
  });

  const purchasePriceCode = `${LEDGER_CODE_PURCHASE_PRICE}_${selectedProperty._id}`;
  const purchasePriceEntries = selectedProperty._id
    ? selectedProperty.accounting?.find((entry) => entry.code.includes(purchasePriceCode))?.involved_parties ?? []
    : [];
  const entryLedger = selectedProperty._id
    ? data?.getOrder.accounting?.find((entry) => entry.code.includes(purchasePriceCode))
    : undefined;

  const selectProperty = (id: string) => {
    if (properties.length > 0) {
      const property = properties.find(
        (orderProperty) => orderProperty._id === id
      );
      setSelectedProperty(property ?? initialValues);
    }
  };
  const { deleteAssociation, deleteAssociationResponse } = useDeleteLedgerAssociation(onAssociationDeleted, () => {
    setLoading(false);
  });
  function onAssociationDeleted(response) {
    if (response) {
      showAlert(capitalize(t('dialogs:order-updated')), 'success');
      deleteAssociationResponse.reset();
    }
    setPendingDeleteEntry(undefined);
    setLoading(false);
  }

  const onRemoveOrderPartyEntry = (entry: AccountingInvolvedParty) => {
    setLoading(true);
    deleteAssociation({
      id: orderId,
      accountingId: entryLedger?._id!,
      kind: entry.kind,
      partyId: entry._id,
    });
  };

  const requestRemoveEntry = (entry: AccountingInvolvedParty) => setPendingDeleteEntry(entry);

  const cancelRemoveEntry = () => {
    if (!isLoading) setPendingDeleteEntry(undefined);
  };

  const confirmRemoveEntry = () => {
    if (pendingDeleteEntry) onRemoveOrderPartyEntry(pendingDeleteEntry);
  };

  return {
    t,
    properties,
    selectedProperty,
    selectProperty,
    purchasePriceEntries,
    entryLedger,
    partyLedgerEntry,
    setPartyLedgerEntry,
    openPartyLedgerModal,
    setOpenPartyLedgerModal,
    onRemoveOrderPartyEntry,
    pendingDeleteEntry,
    requestRemoveEntry,
    cancelRemoveEntry,
    confirmRemoveEntry,
    isLoading,
  };
};

export default useEarnestAndCommissions;
